/**
 * Resting-frame geometry for the gallery floor plan.
 *
 * The plan is sized by a plain contain-fit: the `<img>` is `width/height: auto`
 * bounded by `max-width` / `max-height` of a padded flex box. The browser does
 * the aspect-ratio math during layout, so the resting frame is already correct
 * on the first paint — there is no measurement, no state and no post-load
 * adjustment anywhere in the path, and therefore nothing that can reflow.
 *
 * The only thing this module decides is the *budget* contain-fit is handed: the
 * band between the fixed top chrome (logo bubble / version chips) and the fixed
 * bottom chrome (CTA pill row). One rule does it at every breakpoint — reserve
 * the chrome on both edges and centre the plan in what's left. The band used to
 * run from y = 0, so a height-constrained plan (what a square or landscape plan
 * becomes on a wide desktop viewport) was fitted into a band starting
 * underneath the chrome.
 *
 * Phones are the case worth understanding: the screen is narrow, so anything up
 * to roughly 1:1.6 is *width*-constrained there and the vertical clearances
 * never bind. Only genuinely tall, skinny plans consult the height budget on a
 * phone — which is exactly the set that used to load behind the chrome.
 *
 * All of it is padding on a box that lives *inside* the zoom transform, so at
 * any scale > 1 the padding scales away with the content and the plan still
 * bleeds past the viewport edges exactly as before. This only describes the
 * scale-1 resting frame.
 *
 * These values live outside LayoutVersionSlider so the simulations can assert
 * against the same expressions production renders.
 */

/** Inset of the logo bubble from the top and left viewport edges. */
export const GALLERY_TOP_BAR_INSET_PX = 12;

/** Diameter of the logo bubble, and the height of the version chip strip. */
export const GALLERY_LOGO_BUBBLE_SIZE_PX = 44;

/** Mirrors the `+ 26px` in the CTA row's own `bottom` offset. */
export const GALLERY_CTA_BOTTOM_OFFSET_PX = 26;

/** Mirrors the `max(16px, env(safe-area-inset-bottom, 16px))` floor on that offset. */
export const GALLERY_CTA_SAFE_AREA_FLOOR_PX = 16;

/** Height of one CTA pill — theme `heightRow`. */
export const GALLERY_CTA_PILL_PX = 44;

/** `gap: 1.5` between stacked CTA pills on xs. */
export const GALLERY_CTA_STACK_GAP_PX = 12;

/** MUI default `sm` breakpoint, i.e. the xs ceiling. */
export const GALLERY_SM_BREAKPOINT_PX = 600;

/** Horizontal breathing room around the plan. */
export const GALLERY_FIT_INLINE_PAD_PX = { xs: 18, sm: 36 };

/**
 * From sm up the CTA pills share a single row (`flexDirection: row`). On xs they
 * stack, and the tallest the stack can get is two rows: "Shuffle it", then the
 * Share / I'm Interested pair in its own nested row.
 */
export const GALLERY_CTA_ROWS_SM = 1;
export const GALLERY_CTA_ROWS_XS_MAX = 2;

/**
 * The resting lift: the band is inset from the bottom by at least this much so
 * the plan sits a little above true centre. Kept as a floor rather than a fixed
 * value, because it falls below the CTA row on short viewports.
 */
export const GALLERY_RESTING_LIFT = "12vh";
/** Derived, not hand-synced, so the two can't drift apart. */
export const GALLERY_RESTING_LIFT_RATIO =
  Number.parseFloat(GALLERY_RESTING_LIFT) / 100;

/** Top chrome extent (inset + bubble) plus one inset of breathing room. */
export const GALLERY_FIT_TOP_CLEARANCE_PX =
  GALLERY_TOP_BAR_INSET_PX * 2 + GALLERY_LOGO_BUBBLE_SIZE_PX;

/** Height of a CTA stack of `rows` pills, including the gaps between them. */
export function galleryCtaStackHeightPx(rows) {
  return (
    rows * GALLERY_CTA_PILL_PX +
    Math.max(0, rows - 1) * GALLERY_CTA_STACK_GAP_PX
  );
}

/**
 * Bottom chrome extent above the safe-area floor (offset + pill stack) plus one
 * inset of breathing room. The safe-area floor itself is added by the CSS
 * `max()`, so it isn't included here.
 */
export function galleryFitBottomClearancePx(rows) {
  return (
    GALLERY_CTA_BOTTOM_OFFSET_PX +
    galleryCtaStackHeightPx(rows) +
    GALLERY_TOP_BAR_INSET_PX
  );
}

export const galleryFitTopClearanceExpr = `env(safe-area-inset-top, 0px) + ${GALLERY_FIT_TOP_CLEARANCE_PX}px`;

export const galleryFitBottomClearanceExpr = (rows) =>
  `max(${GALLERY_CTA_SAFE_AREA_FLOOR_PX}px, env(safe-area-inset-bottom, ${GALLERY_CTA_SAFE_AREA_FLOOR_PX}px)) + ${galleryFitBottomClearancePx(
    rows
  )}px`;

/**
 * Padding for the floor plan's framing box. Spread into that box's `sx`.
 *
 * Every value is a constant string. Nothing here is derived from the layout, the
 * current version, the load state or the bitmap, which is what makes toggling
 * Unfurnished ↔ Furnished seamless: the band is byte-identical for every
 * version, so the browser resolves it once in the first layout pass and no later
 * commit can move the plan.
 *
 * That is also why the xs reserve is the *worst case* (two stacked CTA rows)
 * rather than the number of rows a given layout would actually show. Reserving
 * only what's needed would buy a tall plan ~20% more area on a phone, but it
 * would make the band a function of `layoutsVersions.length` — and the gallery
 * paints a cached blob while that is still undefined on the return-from-editor
 * path, so the band would shift under the plan the moment the fetch landed.
 * A stable frame is worth more than the extra pixels.
 *
 * The bottom keeps the percentage lift as a floor as well as a pixel minimum,
 * because `12vh` drops below the CTA row on short viewports — under ~717px tall
 * on desktop, which is an ordinary laptop window.
 */
export const galleryFitSx = {
  pt: `calc(${galleryFitTopClearanceExpr})`,
  pb: {
    xs: `max(${GALLERY_RESTING_LIFT}, ${galleryFitBottomClearanceExpr(
      GALLERY_CTA_ROWS_XS_MAX
    )})`,
    sm: `max(${GALLERY_RESTING_LIFT}, ${galleryFitBottomClearanceExpr(
      GALLERY_CTA_ROWS_SM
    )})`,
  },
};

/**
 * Where the fixed chrome actually ends, for assertions.
 *
 * @param {object} p
 * @param {number} p.viewportHeight
 * @param {number} [p.safeAreaTop]
 * @param {number} [p.safeAreaBottom]
 * @param {number} [p.ctaRows] 1 on sm+, up to 2 on xs where the pills stack.
 */
export function resolveGalleryChrome({
  viewportHeight,
  safeAreaTop = 0,
  safeAreaBottom = 0,
  ctaRows = GALLERY_CTA_ROWS_SM,
}) {
  const topChromeBottom =
    safeAreaTop + GALLERY_TOP_BAR_INSET_PX + GALLERY_LOGO_BUBBLE_SIZE_PX;

  const bottomChromeTop =
    viewportHeight -
    (Math.max(safeAreaBottom, GALLERY_CTA_SAFE_AREA_FLOOR_PX) +
      GALLERY_CTA_BOTTOM_OFFSET_PX +
      galleryCtaStackHeightPx(ctaRows));

  return { topChromeBottom, bottomChromeTop };
}

/**
 * Model of the rect the browser lays the plan out into, resolving the same
 * expressions `getGalleryFitSx` emits. This mirrors CSS contain-fit semantics —
 * including the fact that `max-width`/`max-height` on an auto-sized `<img>`
 * only ever shrink it, never upscale past its intrinsic size — so the geometry
 * simulation can sweep viewports and plan ratios without a browser.
 *
 * @param {object} p
 * @param {number} p.viewportWidth
 * @param {number} p.viewportHeight
 * @param {number} p.planWidth   intrinsic pixel width of the rendition
 * @param {number} p.planHeight  intrinsic pixel height of the rendition
 * @param {number} [p.safeAreaTop]
 * @param {number} [p.safeAreaBottom]
 */
export function resolveGalleryFitRect({
  viewportWidth,
  viewportHeight,
  planWidth,
  planHeight,
  safeAreaTop = 0,
  safeAreaBottom = 0,
}) {
  const isXs = viewportWidth < GALLERY_SM_BREAKPOINT_PX;

  const inlinePad = isXs
    ? GALLERY_FIT_INLINE_PAD_PX.xs
    : GALLERY_FIT_INLINE_PAD_PX.sm;

  const padTop = safeAreaTop + GALLERY_FIT_TOP_CLEARANCE_PX;
  const bottomClearance =
    Math.max(safeAreaBottom, GALLERY_CTA_SAFE_AREA_FLOOR_PX) +
    galleryFitBottomClearancePx(
      isXs ? GALLERY_CTA_ROWS_XS_MAX : GALLERY_CTA_ROWS_SM
    );
  const lift = GALLERY_RESTING_LIFT_RATIO * viewportHeight;
  const padBottom = Math.max(lift, bottomClearance);

  const bandWidth = Math.max(0, viewportWidth - inlinePad * 2);
  const bandHeight = Math.max(0, viewportHeight - padTop - padBottom);

  const widthScale = bandWidth / planWidth;
  const heightScale = bandHeight / planHeight;
  const scale = Math.min(1, widthScale, heightScale);

  const width = planWidth * scale;
  const height = planHeight * scale;

  const top = padTop + bandHeight / 2 - height / 2;
  const left = inlinePad + (bandWidth - width) / 2;

  let constrainedBy = "intrinsic";
  if (scale === heightScale && heightScale <= widthScale)
    constrainedBy = "height";
  else if (scale === widthScale) constrainedBy = "width";

  return {
    top,
    bottom: top + height,
    left,
    right: left + width,
    width,
    height,
    constrainedBy,
    isXs,
  };
}
